import type { GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

interface Pile {
  name: string;
  body: string;
}

const PILES: Pile[] = [
  {
    name: 'The tableau',
    body: 'Ten columns holding fifty-four cards — six each in the first four, five in the rest. Only the bottom card of each is face up; the rest turn over as you clear what covers them.',
  },
  {
    name: 'The stock',
    body: 'The fifty cards left over, dealt ten at a time — one face up onto every column at once. Five deals, and then the whole pack is on the table.',
  },
  {
    name: 'Home',
    body: 'Eight spaces, one per run. A King-to-Ace sequence in a single suit leaves the table for good and fills one. Fill all eight and the game is won.',
  },
];

export function SpiderHowToPlay({ game }: { game: GameSummary }) {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">How to play</p>
        <h1 className="prose__title">{game.name}</h1>
        <p className="prose__lead">
          Build eight King-to-Ace runs, each in one suit, out of two shuffled packs — and clear them
          off the table one at a time.
        </p>
      </header>

      <section className="prose__section">
        <h2>The table</h2>
        <ul className="prose__list">
          {PILES.map((pile) => (
            <li key={pile.name}>
              <div>
                <h3>{pile.name}</h3>
                <p>{pile.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="prose__section">
        <h2>The one rule that matters</h2>
        <p>
          Cards <strong>stack by rank alone</strong>. Any Eight may be laid on any Nine, whatever the
          suits — Spider is generous about what you may build.
        </p>
        <p>
          But a sequence only <strong>moves as a piece if it is also all one suit</strong>. A nine, eight
          and seven of spades travel together; a nine of spades, eight of hearts and seven of spades are
          three separate cards that have to be taken apart one at a time.
        </p>
        <p>
          That gap between what you may build and what you may move is the whole game. Everything else —
          when to deal, which column to empty, whether to bury a card now to free two later — follows
          from it.
        </p>
      </section>

      <section className="prose__section">
        <h2>Suits are the difficulty</h2>
        <p>
          At <strong>one suit</strong> the two rules above coincide: everything in sequence is also in
          suit, so runs always move whole. It is a gentle puzzle and most deals come out.
        </p>
        <p>
          At <strong>two</strong>, roughly half of what you build will not travel. At{' '}
          <strong>four</strong>, a tidy-looking column of six is usually four separate runs, and
          untangling it is most of what you spend the game doing. Same deal, same rules, three quite
          different games.
        </p>
      </section>

      <section className="prose__section">
        <h2>Gaps, and the price of them</h2>
        <p>
          An empty column is the most useful thing on the table: anything may move into it, including a
          long run you need out of the way. There is no King rule here.
        </p>
        <p>
          It has a price, though. The stock <strong>will not deal while a column stands empty</strong>,
          so you cannot bank a gap and keep turning cards — you have to spend it, or fill it back in.
          That tension is what stops the stock being an escape from a board you have tangled. If you
          would rather it were not, the setting is there to switch off.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary btn--large" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      </div>
    </main>
  );
}
